// src/reports/ExecutiveNarrative.tsx (Questionnaire-aware: Telecom + AI/LLM)
import * as React from "react";

type FnKey = "Govern" | "Identify" | "Protect" | "Detect" | "Respond" | "Recover";
export type FnScore = { fn: FnKey; you: number }; // 0–100

function maturityOf(v: number) {
  if (v >= 80) return "Optimized";
  if (v >= 60) return "Defined/Managed";
  if (v >= 40) return "Developing";
  if (v >  0)  return "Initial";
  return "Nonexistent";
}

/* ─────────────────────────────
   TELECOM-SPECIFIC NARRATIVE LOGIC
────────────────────────────── */
function telecomOverallNarrative(org: string, score: number) {
  const m = maturityOf(score);
  if (m === "Optimized") {
    return (
      <>
        {org} demonstrates a highly mature cybersecurity posture. Security is embedded across network operations,
        customer-facing systems, and the supply chain. Automated detection and response capabilities are integrated
        within the NOC and SOC, and resilience testing validates both OSS/BSS and infrastructure layers. The current
        focus should be on sustaining automation, validating control efficacy against evolving 5G and IoT threats, and
        leveraging analytics to reduce mean time to detect and respond (MTTD/MTTR) across hybrid environments.
      </>
    );
  }
  if (m === "Defined/Managed") {
    return (
      <>
        {org} has established a well-structured cybersecurity program aligned with operational technology (OT) and IT
        processes. Governance and policies are standardized across business units, and network segmentation and
        redundancy are actively managed. Continued maturity will come from deepening automation within the SOC, improving
        cross-domain coordination between NOC, SOC, and field operations, and enhancing supplier assurance programs to
        align with telecom-specific regulations such as GSMA NESAS and regional data protection laws.
      </>
    );
  }
  if (m === "Developing") {
    return (
      <>
        {org} has implemented foundational controls and is actively maturing its governance and risk management
        practices. Network and service assurance processes are documented but may lack integration with enterprise risk
        management. Priorities include expanding asset visibility across network elements, strengthening configuration
        management in OSS/BSS environments, and formalizing threat detection and incident reporting between network
        operations and cybersecurity teams.
      </>
    );
  }
  if (m === "Initial") {
    return (
      <>
        {org} is developing awareness of its cybersecurity obligations within critical telecom infrastructure. Ad-hoc
        practices exist across IT and network teams, but unified governance is limited. The immediate focus should be on
        mapping critical assets (core, transport, access), enabling MFA for remote and administrative access, establishing
        centralized logging within the NOC, and drafting an incident response playbook aligned with telecom continuity
        and customer-impact mitigation standards.
      </>
    );
  }
  return (
    <>
      {org} has not yet implemented repeatable cybersecurity processes across telecom and enterprise domains. Initial
      steps should include identifying core network components and data repositories, implementing baseline protections
      such as firewalls and endpoint security, and documenting response and recovery procedures for outages or security
      events that could affect customer service continuity.
    </>
  );
}

/* ─────────────────────────────
   AI/LLM-SPECIFIC NARRATIVE LOGIC
────────────────────────────── */
function aiOverallNarrative(org: string, score: number) {
  const m = maturityOf(score);
  if (m === "Optimized") {
    return (
      <>
        {org} demonstrates a highly mature AI security and governance posture. Controls for prompt injection,
        output validation, data protection, model integrity, and third-party AI oversight are consistently applied
        and continuously validated. The focus should be on sustaining assurance through adversarial testing,
        governance automation, and adapting to evolving AI-specific regulatory requirements and threat landscapes.
      </>
    );
  }
  if (m === "Defined/Managed") {
    return (
      <>
        {org} has established a structured AI governance and security program covering model lifecycle, data handling,
        and operational controls. Policies are documented, roles are assigned, and risk-based oversight is operational.
        Continued maturity should focus on deepening monitoring and detection, strengthening supply chain assurance for
        AI providers, and integrating AI risk management into enterprise-wide governance and audit reporting.
      </>
    );
  }
  if (m === "Developing") {
    return (
      <>
        {org} has implemented foundational AI security controls and is actively maturing governance, risk assessment,
        and operational safeguards for LLM and generative AI systems. Priorities include expanding prompt injection
        defenses, formalizing training data governance, strengthening output validation across use cases, and
        establishing structured adversarial testing before production deployments.
      </>
    );
  }
  if (m === "Initial") {
    return (
      <>
        {org} is developing awareness of AI-specific security risks and governance requirements. Ad-hoc practices
        exist for some AI use cases, but unified governance, risk assessment, and technical controls are limited.
        The immediate focus should be on establishing an AI governance policy, creating an AI system inventory,
        defining acceptable use standards, and implementing basic prompt injection and output validation controls
        for production-facing AI systems.
      </>
    );
  }
  return (
    <>
      {org} has not yet implemented structured security or governance processes for AI and LLM systems. Initial
      steps should include identifying all AI systems in use, establishing ownership and accountability, defining
      acceptable use policies, and implementing baseline controls for prompt security, output validation, sensitive
      data protection, and third-party AI due diligence.
    </>
  );
}

/* ─────────────────────────────
   TELECOM-SPECIFIC FUNCTION IMPORTANCE
────────────────────────────── */
const TELECOM_FUNCTION_IMPORTANCE: Record<FnKey, string> = {
  Govern:
    "Governance establishes the oversight, accountability, and risk strategy that guide cybersecurity decisions across the organization. It ensures leadership understands regulatory obligations, risk appetite, and resource allocation for security controls.",
  Identify:
    "In telecom, identification establishes visibility across network assets, subscriber data, and interconnection points. It ensures leadership understands which systems underpin critical services such as voice, data, and signaling.",
  Protect:
    "Protection mechanisms—like segmentation, encryption, and privileged access control—safeguard the confidentiality and availability of customer and infrastructure data across core, transport, and access networks.",
  Detect:
    "Detection capabilities enable continuous monitoring across OSS/BSS, NOC, and SOC layers to uncover anomalies, configuration drifts, or malicious activity that could disrupt communications or degrade service quality.",
  Respond:
    "Response processes coordinate internal teams, vendors, and regulators during incidents. Effective response limits customer impact, supports mandatory notifications, and preserves forensic evidence for post-event review.",
  Recover:
    "Recovery ensures continuity of voice, data, and digital services following outages or breaches. It includes tested restoration of critical platforms, cloud workloads, and interconnect gateways to restore subscriber trust and meet SLAs.",
};

/* ─────────────────────────────
   AI/LLM-SPECIFIC FUNCTION IMPORTANCE
────────────────────────────── */
const AI_FUNCTION_IMPORTANCE: Record<FnKey, string> = {
  Govern:
    "Governance establishes oversight, accountability, and risk strategy for AI and LLM systems. It ensures leadership understands AI-specific risks, regulatory obligations, acceptable use, and resource allocation for AI security controls.",
  Identify:
    "Identification creates visibility across AI systems, models, data pipelines, and dependencies. It ensures the organization understands which AI components are in use, their risk classification, and their role in business operations.",
  Protect:
    "Protection mechanisms—including prompt injection defenses, output validation, data handling controls, and least-privilege agent restrictions—safeguard AI systems from misuse, manipulation, and unauthorized access.",
  Detect:
    "Detection capabilities enable monitoring of AI usage, misuse, anomalous behavior, and policy violations across model interactions, tool calls, and data flows to identify threats before they cause material harm.",
  Respond:
    "Response processes coordinate teams and providers during AI-specific incidents such as prompt injection, data leakage, unsafe outputs, or model compromise. Effective response limits impact and preserves evidence for investigation.",
  Recover:
    "Recovery ensures continuity of AI-dependent services following incidents or failures. While AI systems may have limited traditional recovery needs, model rollback, service restoration, and lessons learned are essential for resilience.",
};

/* ─────────────────────────────
   Key Risk Statement templates
────────────────────────────── */
const AI_RISK_TEMPLATES: Record<string, string> = {
  Govern: "The organization currently lacks defined governance and oversight for AI systems, increasing the risk of uncontrolled deployment, unclear accountability, and unmanaged regulatory exposure.",
  Identify: "The organization lacks a complete inventory and risk classification for AI systems, creating blind spots that could allow insecure or unapproved models to operate without oversight.",
  Protect: "Technical safeguards protecting AI systems are limited, increasing the risk of prompt injection attacks, data leakage, and unauthorized manipulation of model behavior.",
  Detect: "Monitoring and detection capabilities for AI activity are insufficient, increasing the risk that model misuse, adversarial prompts, or abnormal behavior may go unnoticed.",
  Respond: "The organization lacks a defined response process for AI-related security incidents, increasing the risk of delayed containment and prolonged impact from AI system compromise.",
  Recover: "The organization lacks tested recovery and continuity processes for AI-dependent services, increasing the risk of prolonged service disruption following an AI system failure or compromise.",
};

const TELECOM_RISK_TEMPLATES: Record<string, string> = {
  Govern: "The organization currently lacks defined cybersecurity governance and oversight across telecom operations, increasing the risk of fragmented security decisions and unmanaged regulatory exposure.",
  Identify: "The organization lacks comprehensive visibility into critical network assets and data flows, creating blind spots that could allow vulnerabilities to persist undetected across infrastructure layers.",
  Protect: "Technical safeguards across network and customer-facing systems are limited, increasing the risk of unauthorized access, data breaches, and service disruption.",
  Detect: "Monitoring and detection capabilities across NOC and SOC environments are insufficient, increasing the risk that anomalies, configuration drifts, or malicious activity may go unnoticed.",
  Respond: "The organization lacks a coordinated incident response process, increasing the risk of delayed containment and prolonged customer impact during security events.",
  Recover: "The organization lacks tested recovery procedures for critical telecom platforms, increasing the risk of extended service outages and failure to meet SLA commitments following incidents.",
};

export function generateKeyRiskStatement(
  functionScores: FnScore[],
  overallScore: number,
  questionnaireId?: string | null,
): string | null {
  const validScores = functionScores;
  if (!validScores.length) return null;

  const lowest = [...validScores].sort((a, b) => a.you - b.you)[0];
  if (!lowest) return null;

  const isAI = questionnaireId === "ai_llm_self_v1";
  const templates = isAI ? AI_RISK_TEMPLATES : TELECOM_RISK_TEMPLATES;
  const riskText = templates[lowest.fn];
  if (!riskText) return null;

  const prefix =
    overallScore < 25
      ? isAI
        ? "The organization is at an early stage of AI security maturity. "
        : "The organization is at an early stage of cybersecurity maturity. "
      : "";

  return `${prefix}${riskText}`;
}

/* ─────────────────────────────
   Dynamic strengths/weaknesses line
────────────────────────────── */
function dynamicSummary(scores: FnScore[]) {
  const clean = scores
    .map(s => ({ fn: s.fn, you: Number.isFinite(s.you) ? Math.max(0, Math.min(100, Math.round(s.you))) : 0 }))
    .filter(s => typeof s.you === "number");

  if (!clean.length) return null;

  const sorted = [...clean].sort((a,b) => b.you - a.you);
  const highestScore = sorted[0].you;
  const lowestScore = sorted[sorted.length - 1].you;

  // Group all functions sharing the highest score
  const topFns = sorted.filter(s => s.you === highestScore);
  // Group all functions sharing the lowest score
  const bottomFns = [...clean].sort((a,b) => a.you - b.you).filter(s => s.you === lowestScore);

  // If all functions have the same score, skip the summary
  if (highestScore === lowestScore) return null;

  const fmt = (arr: {fn: FnKey; you: number}[]) => {
    const names = arr.map(s => s.fn);
    const pct = arr[0].you;
    if (names.length === 1) return `${names[0]} (${pct}%)`;
    if (names.length === 2) return `${names[0]} and ${names[1]} (${pct}%)`;
    return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]} (${pct}%)`;
  };

  const strengthLabel = topFns.length > 1 ? "Strongest areas are" : "Strongest area is";
  const weaknessLabel = bottomFns.length > 1 ? "priority improvement areas include" : "the priority improvement area is";

  return (
    <>
      {strengthLabel} <strong>{fmt(topFns)}</strong>, while {weaknessLabel}{" "}
      <strong>{fmt(bottomFns)}</strong>. Use these insights to focus near-term remediation and validation efforts.
    </>
  );
}

/* ─────────────────────────────
   Visual style tokens for Function pills/cards
────────────────────────────── */
const COLOR: Record<FnKey, {chip: string; border: string; bgSoft: string}> = {
  Govern:   { chip: "bg-purple-600 text-white",  border: "border-purple-200",  bgSoft: "bg-purple-50" },
  Identify: { chip: "bg-sky-600 text-white",     border: "border-sky-200",     bgSoft: "bg-sky-50" },
  Protect:  { chip: "bg-emerald-600 text-white", border: "border-emerald-200", bgSoft: "bg-emerald-50" },
  Detect:   { chip: "bg-amber-600 text-white",   border: "border-amber-200",   bgSoft: "bg-amber-50" },
  Respond:  { chip: "bg-rose-600 text-white",    border: "border-rose-200",    bgSoft: "bg-rose-50" },
  Recover:  { chip: "bg-indigo-600 text-white",  border: "border-indigo-200",  bgSoft: "bg-indigo-50" },
};

/* ─────────────────────────────
   Component
────────────────────────────── */
export function ExecutiveNarrative({
  orgName,
  dateStr,
  functionScores,
  questionnaireId,
}: {
  orgName: string;
  dateStr?: string;
  functionScores: FnScore[];
  questionnaireId?: string | null;
}) {
  const isAI = questionnaireId === "ai_llm_self_v1";
  const overallNarrative = isAI ? aiOverallNarrative : telecomOverallNarrative;
  const functionImportance = isAI ? AI_FUNCTION_IMPORTANCE : TELECOM_FUNCTION_IMPORTANCE;
  const sectionTitle = isAI
    ? "About the NIST Cybersecurity Framework Functions (AI/LLM)"
    : "About the NIST Cybersecurity Framework Functions (Telecom)";

  // Always display all 6 NIST CSF functions regardless of score
  const displayFunctions: FnKey[] = ["Govern","Identify","Protect","Detect","Respond","Recover"];

  const vals = functionScores.map(s => s.you).filter(n => Number.isFinite(n));
  const overall = vals.length ? Math.round(vals.reduce((a,b)=>a+b, 0) / vals.length) : 0;

  const dyn = dynamicSummary(functionScores);
  const keyRisk = generateKeyRiskStatement(functionScores, overall, questionnaireId);

  return (
    <section className="executive-summary print-card-reset avoid-break rounded-xl border p-3 mb-1">
      <div>
        <h2 className="text-sm font-bold">Executive Summary</h2>
      </div>

      {/* Key Risk Statement */}
      {keyRisk && (
        <div className="mt-1.5 rounded-lg bg-muted/60 border border-border px-3 py-2">
          <p className="text-xs leading-relaxed">
            <strong>Key Risk:</strong> {keyRisk}
          </p>
        </div>
      )}

      {/* Expanded, band-specific overview */}
      <p className="mt-1.5 text-xs leading-relaxed">
        {overallNarrative(orgName, overall)}
      </p>

      {/* Dynamic strengths/weaknesses line (optional) */}
      {dyn && <p className="mt-1 text-xs leading-relaxed">{dyn}</p>}

      {/* ── Styled "About the Functions" with colorful pills/cards ── */}
      <div className="mt-3 about-nist">
        {/* Section pill title */}
        <div className="about-nist__title inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-slate-900 text-white print-force-bg">
          {sectionTitle}
        </div>

        {/* Function cards */}
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 about-nist__grid">
          {displayFunctions.map((fn) => {
            const c = COLOR[fn];
            return (
              <div
                key={fn}
                className={`rounded-lg border ${c.border} ${c.bgSoft} p-2 print-card-reset`}
              >
                <div className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${c.chip} print-force-bg`}>
                  {fn}
                </div>
                <p className="mt-1 text-xs leading-snug text-slate-800">
                  {functionImportance[fn]}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export { type FnKey };
